// src/admin/screens/collections/CollectionsQueueScreen.tsx
// Agents' workbench: every active delinquent loan, sorted by days_overdue
// descending. The top bucket strip mirrors the design's "0-30 / 31-60 / etc."
// summary tiles; chip filters set ?bucket= which the hook translates to
// server-side day ranges. Clicking a row opens the case detail screen.
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DataTable, { type Column } from '@admin/components/DataTable';
import FilterChip from '@admin/components/FilterChip';
import StatusChip from '@admin/components/StatusChip';
import Money from '@admin/components/Money';
import SectionHead from '@admin/components/SectionHead';
import Avatar from '@admin/components/Avatar';
import { useCollectionsQueue, type Bucket, type CollectionsRow } from '@admin/hooks/useCollectionsQueue';
import { BUCKET_LABEL, CHANNEL_LABEL, OUTCOME_LABEL } from '@admin/utils/collections';
import { LOAN_STATUS_LABEL, LOAN_STATUS_TONE } from '@admin/utils/loanStatus';
import { formatZambiaPhone } from '@admin/utils/phone';
import { formatCatDate } from '@admin/utils/dates';
import { formatKwacha } from '@admin/utils/money';
import type { Ngwee } from '@/lib/types';
import styles from './CollectionsQueueScreen.module.css';

const BUCKETS: { value: Bucket | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: '0_14d', label: BUCKET_LABEL['0_14d'] },
  { value: '14_30d', label: BUCKET_LABEL['14_30d'] },
  { value: '30_60d', label: BUCKET_LABEL['30_60d'] },
  { value: '60_90d', label: BUCKET_LABEL['60_90d'] },
  { value: 'default', label: BUCKET_LABEL['default'] },
];

function parseBucket(raw: string | null): Bucket | 'all' {
  if (raw === '0_14d' || raw === '14_30d' || raw === '30_60d' || raw === '60_90d' || raw === 'default') return raw;
  return 'all';
}

export default function CollectionsQueueScreen() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const bucket = parseBucket(params.get('bucket'));

  const filter = bucket === 'all' ? {} : { bucket };
  const { data, isLoading, error } = useCollectionsQueue(filter);
  const rowCount = data?.length ?? 0;

  // Build summary tiles by bucket from the unfiltered loaded data — close
  // enough for the at-a-glance view; precise figures come from the queue
  // itself filtered server-side.
  const allRows = data ?? [];
  const tile = (label: string, predicate: (r: CollectionsRow) => boolean, tone: 'warning' | 'danger') => {
    const rows = allRows.filter(predicate);
    const sum = rows.reduce((s, r) => s + Number(r.outstanding_ngwee ?? 0), 0);
    return { label, count: rows.length, total: formatKwacha(sum as Ngwee, { decimals: 0 }), tone };
  };
  const tiles = [
    tile('0–14 days',  (r) => r.days_overdue >= 0 && r.days_overdue <= 14,  'warning'),
    tile('14–30 days', (r) => r.days_overdue > 14 && r.days_overdue <= 30, 'warning'),
    tile('30–60 days', (r) => r.days_overdue > 30 && r.days_overdue <= 60, 'warning'),
    tile('60–90 days', (r) => r.days_overdue > 60 && r.days_overdue <= 90, 'danger'),
    tile('90+ days',   (r) => r.days_overdue > 90,                          'danger'),
  ];

  const columns = useMemo<Column<CollectionsRow>[]>(
    () => [
      {
        key: 'loan',
        header: 'Loan',
        render: (r) => (
          <span className="mono" style={{ fontWeight: 600, color: 'var(--c-p800)' }}>{r.loan_number}</span>
        ),
        width: '130px',
      },
      {
        key: 'borrower',
        header: 'Borrower',
        render: (r) => {
          const name = r.borrower_name ?? '—';
          return (
            <div className={styles.borrowerCell}>
              <Avatar name={name} size={28} />
              <div>
                <div className={styles.borrowerName}>{name}</div>
                <span className={styles.borrowerSub}>{formatZambiaPhone(r.borrower_phone)}</span>
              </div>
            </div>
          );
        },
      },
      {
        key: 'days',
        header: 'Overdue',
        align: 'right',
        render: (r) => {
          const cls = r.days_overdue >= 30 ? styles.daysRed : r.days_overdue >= 14 ? styles.daysAmber : styles.daysGrey;
          return <span className={cls}>{r.days_overdue}d</span>;
        },
      },
      {
        key: 'owed',
        header: 'Outstanding',
        align: 'right',
        render: (r) => <Money amount={r.outstanding_ngwee} />,
      },
      {
        key: 'contact',
        header: 'Last contact',
        render: (r) =>
          r.last_contact_at ? (
            <div className={styles.contactCell}>
              <span>{formatCatDate(r.last_contact_at)}</span>
              <span className={styles.contactSub}>
                {r.last_contact_channel ? CHANNEL_LABEL[r.last_contact_channel] : '—'}
                {r.last_contact_outcome ? ` · ${OUTCOME_LABEL[r.last_contact_outcome]}` : ''}
              </span>
            </div>
          ) : (
            <span style={{ color: 'var(--c-text-dim)' }}>Never</span>
          ),
      },
      {
        key: 'ptp',
        header: 'Promise',
        render: (r) =>
          r.active_ptp_date ? (
            <div className={styles.contactCell}>
              <span>{r.active_ptp_date}</span>
              <span className={styles.contactSub}>
                <Money amount={r.active_ptp_amount_ngwee} />
              </span>
            </div>
          ) : (
            <span style={{ color: 'var(--c-text-dim)' }}>—</span>
          ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) => <StatusChip label={LOAN_STATUS_LABEL[r.status]} tone={LOAN_STATUS_TONE[r.status]} />,
      },
    ],
    [],
  );

  const setBucket = (next: Bucket | 'all') => {
    const p = new URLSearchParams(params);
    if (next === 'all') p.delete('bucket');
    else p.set('bucket', next);
    setParams(p, { replace: true });
  };

  return (
    <div className={styles.page}>
      <SectionHead
        eyebrow="Collections"
        title="Overdue loans"
        description={`${rowCount} loan${rowCount === 1 ? '' : 's'} in collections`}
      />

      <div className={styles.bucketGrid}>
        {tiles.map((t) => (
          <div key={t.label} className={styles.bucketTile}>
            <div className={styles.bucketLabel}>{t.label}</div>
            <div className={styles.bucketRow}>
              <span className={styles.bucketValue}>{t.total}</span>
              <span className={`${styles.bucketCount} ${styles[t.tone]}`}>
                {t.count} loan{t.count === 1 ? '' : 's'}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.chipsRow}>
        {BUCKETS.map((b) => (
          <FilterChip
            key={b.value}
            active={b.value === bucket}
            onClick={() => setBucket(b.value)}
            label={b.label}
          />
        ))}
      </div>

      <DataTable
        rows={data}
        columns={columns}
        isLoading={isLoading}
        error={error ? (error as Error).message : null}
        emptyLabel="No cases in this bucket."
        rowKey={(r) => r.loan_id}
        onRowClick={(r) => navigate(`/collections/${r.loan_id}`)}
      />
    </div>
  );
}
